import React, { useState } from 'react';
import { useStore } from '../../contexts/StoreContext';
import { Field } from '../ui/Field';
import { ConsentChecks } from '../ConsentChecks';
import { LeadSuccess } from '../LeadSuccess';
import { banks } from '../../data/options';
import { Lead } from '../../types/lead';
import { cleanText, formatCuil, isValidCuil, isValidEmail, isValidName, isValidPhone, normalizePhone } from '../../utils/validation';
type Errors = Partial<Record<'bank' | 'cuil' | 'firstName' | 'lastName' | 'email' | 'phone' | 'consent', string>>;
export function BankBenefitsForm() {
  const {
    addLead
  } = useStore();
  const [bank, setBank] = useState('');
  const [cuil, setCuil] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [contact, setContact] = useState(true);
  const [errors, setErrors] = useState<Errors>({});
  const [lead, setLead] = useState<Lead | null>(null);
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const er: Errors = {};
    if (!bank) er.bank = 'Elegí tu banco.';
    if (!isValidCuil(cuil)) er.cuil = 'Ingresá un CUIL válido (11 dígitos).';
    if (!isValidName(firstName)) er.firstName = 'Ingresá tu nombre.';
    if (!isValidName(lastName)) er.lastName = 'Ingresá tu apellido.';
    if (!isValidEmail(email)) er.email = 'Ingresá un email válido.';
    if (!isValidPhone(phone)) er.phone = 'Ingresá un teléfono con código de área.';
    if (!consent) er.consent = 'Necesitamos tu consentimiento para continuar.';
    setErrors(er);
    if (Object.keys(er).length) return;
    setLead(addLead({
      channel: 'banco',
      name: `${cleanText(firstName, 40)} ${cleanText(lastName, 40)}`,
      phone: normalizePhone(phone),
      email: cleanText(email, 120),
      contactPref: 'whatsapp',
      vehicleId: null,
      vehicleLabel: null,
      price: null,
      down: null,
      term: null,
      simulation: null,
      message: `Solicita beneficios de financiación con ${bank}.`,
      credit: null,
      bank,
      cuil: cuil.replace(/\D/g, ''),
      consents: {
        dataProcessing: consent,
        contact,
        at: new Date().toISOString()
      }
    }));
  };
  if (lead) {
    return <LeadSuccess lead={lead} data-id="element-589" data-mp="2kx5hdg7pogy5">Un asesor te enviará los beneficios y condiciones vigentes de {lead.bank} para tu 0 km.</LeadSuccess>;
  }
  return <form onSubmit={onSubmit} noValidate className="space-y-5" data-id="element-590" data-mp="2fzc8ugixraup">
      <fieldset data-id="element-591" data-mp="2bvxs1nn6aqg7">
        <legend className="label" data-id="element-592" data-mp="2bve9c4pp7y14">Elegí tu banco</legend>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-id="element-593" data-mp="2bya5bnhzkcmx">
          {banks.map(b => <label key={b} className={`cursor-pointer rounded-xl border px-3 py-2.5 text-center text-sm font-medium transition-colors duration-150 ${bank === b ? 'border-neon bg-neon/15 text-white shadow-[0_0_12px_rgba(20,99,255,0.5)]' : 'border-line text-mist hover:border-neon/50 hover:text-white'}`} data-id="element-594" data-mp="26zp3xkqzzmww">
              <input type="radio" name="bank" value={b} checked={bank === b} onChange={() => setBank(b)} className="sr-only" data-id="element-595" data-mp="3c5p1sa50qcez" />
              {b}
            </label>)}
        </div>
        {errors.bank && <p className="mt-1 text-xs text-danger" role="alert" data-id="element-596" data-mp="3kxr1253cpmno">{errors.bank}</p>}
      </fieldset>

      <Field label="CUIL" htmlFor="bk-cuil" error={errors.cuil} data-id="element-597" data-mp="3puzbfji9cmed">
        <input id="bk-cuil" className="input font-mono" inputMode="numeric" placeholder="20-12345678-9" value={cuil} onChange={e => setCuil(formatCuil(e.target.value))} data-id="element-598" data-mp="3uuvf1hn1mbte" />
      </Field>
      <div className="grid gap-4 sm:grid-cols-2" data-id="element-599" data-mp="7qfieyv9g4k2">
        <Field label="Nombre" htmlFor="bk-first" error={errors.firstName} data-id="element-600" data-mp="7p4gl4ldl6vl">
          <input id="bk-first" className="input" value={firstName} onChange={e => setFirstName(e.target.value)} autoComplete="given-name" maxLength={40} data-id="element-601" data-mp="25gyy8r81urln" />
        </Field>
        <Field label="Apellido" htmlFor="bk-last" error={errors.lastName} data-id="element-602" data-mp="1e7m1p8wavy8y">
          <input id="bk-last" className="input" value={lastName} onChange={e => setLastName(e.target.value)} autoComplete="family-name" maxLength={40} data-id="element-603" data-mp="1e9p1gkn995qv" />
        </Field>
        <Field label="Email" htmlFor="bk-email" error={errors.email} data-id="element-604" data-mp="1o4gz1ogu7sln">
          <input id="bk-email" type="email" className="input" value={email} onChange={e => setEmail(e.target.value)} autoComplete="email" maxLength={120} data-id="element-605" data-mp="1j673vdv44s01" />
        </Field>
        <Field label="Teléfono" htmlFor="bk-phone" error={errors.phone} data-id="element-606" data-mp="veru5010b8er">
          <input id="bk-phone" className="input" inputMode="tel" value={phone} onChange={e => setPhone(e.target.value)} autoComplete="tel" maxLength={20} data-id="element-607" data-mp="qjuqk8mhxr99" />
        </Field>
      </div>
      <ConsentChecks consent={consent} contact={contact} onConsent={setConsent} onContact={setContact} error={errors.consent} purpose="informarme los beneficios de financiación de mi banco" data-id="element-608" data-mp="10ffn3r8spzbm" />
      <button type="submit" className="btn btn-primary h-12 w-full" data-id="element-609" data-mp="7qqt0c65qu9d">Quiero conocer mis beneficios</button>
      <p className="text-[11px] text-mist" data-id="element-610" data-mp="2je3som1osluq">
        VCARS no consulta tu situación crediticia con este formulario. Los beneficios, tasas y montos dependen de cada banco y están sujetos a aprobación crediticia.
      </p>
    </form>;
}